import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import RentalsCarousel from '@/components/common/SimilarRentalsCarousel/RentalsCarousel';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { EListingSource } from '@/services/analytics/listings/types';
import { IData as IRental } from '@/services/types/search/rentals/id';
import { mapRentalsToTiles } from '@/utility/mapSearchResultToTile';

type TSearchQuerySectionProps = {
  section_type: 'search_query';
  title: string;
  title_type?: 'header' | 'subtitle';
  image?: string;
  content: string;
  search_query_url?: string;
  search_query_data?: IRental[];
  button_cta?: string;
  button_url?: string;
  background?: string;
};

export const SearchQuerySection = ({
  title,
  title_type = 'header',
  content,
  search_query_data,
  button_cta,
  button_url,
}: TSearchQuerySectionProps) => {
  const intl = useIntl();

  const [isClamped, setIsClamped] = useState(true);

  return (
    <div className="flex flex-col gap-6">
      <div className="lg:max-w-60 lg:mx-auto">
        <Heading
          variant={title_type === 'subtitle' ? EHeadingStyleVariant.H6 : EHeadingStyleVariant.H4}
          component={title_type === 'subtitle' ? 'h6' : 'h2'}
          className="mb-6 text-center">
          {title}
        </Heading>

        <Text
          component="div"
          variant={ETextStyleVariant.MediumRegular}
          className={cn('[&>p]:mb-2', { ['line-clamp-4']: isClamped })}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        <Link
          textVariant={ETextStyleVariant.MediumBold}
          label={
            isClamped
              ? intl.formatMessage({ defaultMessage: 'Read more', id: 'S++WdB' })
              : intl.formatMessage({ defaultMessage: 'Read less', id: 'Ed2ywJ' })
          }
          onClick={() => setIsClamped(!isClamped)}
        />
      </div>

      {!!search_query_data?.length && <SearchQueryCarousel rentals={search_query_data} />}

      {button_cta && button_url && (
        <div className="flex justify-center">
          <Button
            href={button_url}
            label={button_cta}
            variant={EButtonColorVariant.Primary}
            className="w-full md:w-auto"
          />
        </div>
      )}
    </div>
  );
};

type TSearchQueryCarouselProps = {
  rentals: IRental[];
};

const SearchQueryCarousel = ({ rentals }: TSearchQueryCarouselProps) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  const userFavorites = useSelector(getUserFavorites);

  const rentalsList = useMemo(() => {
    return mapRentalsToTiles({
      rentals: rentals || [],
      favorites: userFavorites,
      queryParams: router.query,
    });
  }, [rentals, userFavorites, router.query]);

  return (
    <RentalsCarousel
      maxCols={1}
      verticalScrollGrid
      rentalsList={rentalsList}
      eventSource={EListingSource.EXPLORE_THIS_VEHICLE}
      isRow={isMobile}
    />
  );
};
